import React, { useState, useEffect } from 'react';
import api from '../../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { 
  Plus, FileText, DollarSign, AlertTriangle, 
  CheckCircle, Send, Eye, MoreHorizontal, Search,
  Users, TrendingUp, ChevronRight, Settings, Palette, CreditCard
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import ClientsDialog from './ClientsDialog';
import InvoiceDialog from './InvoiceDialog';
import InvoiceViewDialog from './InvoiceViewDialog';
import InvoiceSettingsDialog from './InvoiceSettingsDialog';
import InvoiceThemeDialog from './InvoiceThemeDialog';
import StripeConnectDialog from './StripeConnectDialog';
import StatementsDialog from './StatementsDialog';
import StatementViewDialog from './StatementViewDialog';
import SendInvoiceDialog from './SendInvoiceDialog';
import SendStatementDialog from './SendStatementDialog';

// Light mode compatible status colors with better contrast
const statusConfig = {
  draft: { label: 'Draft', color: 'bg-slate-600 dark:bg-slate-500', textColor: 'text-white', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-600 dark:bg-blue-500', textColor: 'text-white', icon: Send },
  viewed: { label: 'Viewed', color: 'bg-purple-600 dark:bg-purple-500', textColor: 'text-white', icon: Eye },
  paid: { label: 'Paid', color: 'bg-green-600 dark:bg-green-500', textColor: 'text-white', icon: CheckCircle },
  overdue: { label: 'Overdue', color: 'bg-red-600 dark:bg-red-500', textColor: 'text-white', icon: AlertTriangle },
  void: { label: 'Void', color: 'bg-gray-600 dark:bg-gray-500', textColor: 'text-white', icon: FileText },
};

export default function InvoicesTab({ profile }) {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Dialog states
  const [showClientsDialog, setShowClientsDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showThemeDialog, setShowThemeDialog] = useState(false);
  const [showStripeConnectDialog, setShowStripeConnectDialog] = useState(false);
  const [showStatementsDialog, setShowStatementsDialog] = useState(false);
  const [viewingStatement, setViewingStatement] = useState(null);
  const [sendingStatement, setSendingStatement] = useState(null);
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [stripeConnectStatus, setStripeConnectStatus] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [autoOpenPayment, setAutoOpenPayment] = useState(false);

  useEffect(() => {
    loadData();
    loadInvoiceSettings();
    loadStripeConnectStatus();
  }, []);

  const loadInvoiceSettings = async () => {
    try {
      const res = await api.getInvoiceSettings();
      setInvoiceSettings(res.data);
    } catch (error) {
      console.error('Failed to load invoice settings:', error);
    }
  };

  const loadStripeConnectStatus = async () => {
    try {
      const res = await api.getStripeConnectStatus();
      setStripeConnectStatus(res.data);
    } catch (error) {
      console.error('Failed to load Stripe Connect status:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, clientsRes, statsRes] = await Promise.all([
        api.getInvoices(),
        api.getInvoiceClients(),
        api.getInvoiceStats()
      ]);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
      setStats(statsRes.data);
      // Keep the open View dialog's invoice fresh after any action (no re-open needed)
      setSelectedInvoice(prev => prev ? (invoicesRes.data.find(i => i.id === prev.id) || prev) : prev);
    } catch (error) {
      console.error('Failed to load invoice data:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvoice = () => {
    if (clients.length === 0) {
      toast.error('Please add a client first');
      setShowClientsDialog(true);
      return;
    }
    setEditingInvoice(null);
    setShowInvoiceDialog(true);
  };

  const handleEditInvoice = (invoice) => {
    if (invoice.status === 'void') {
      toast.error('Cannot edit voided invoices');
      return;
    }
    setEditingInvoice(invoice);
    setShowInvoiceDialog(true);
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowViewDialog(true);
  };

  const handleDuplicateInvoice = async (invoice) => {
    try {
      await api.duplicateInvoice(invoice.id);
      toast.success('Invoice duplicated');
      loadData();
    } catch (error) {
      toast.error('Failed to duplicate invoice');
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    if (invoice.status !== 'draft') {
      toast.error('Can only delete draft invoices');
      return;
    }
    if (!window.confirm('Delete this draft invoice?')) return;
    
    try {
      await api.deleteInvoice(invoice.id);
      toast.success('Invoice deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleMarkPaid = (invoice) => {
    // Open the invoice with the Record Payment dialog so the user picks the
    // actual method (Check, Card, Cash, etc.) instead of a generic "Marked as paid".
    setSelectedInvoice(invoice);
    setAutoOpenPayment(true);
    setShowViewDialog(true);
  };

  const handleSendInvoice = async (invoice) => {
    // Open send dialog instead of sending immediately
    const client = clients.find(c => c.id === invoice.client_id);
    setSendingInvoice({ ...invoice, client });
  };

  // Filter, sort (newest first), and paginate invoices
  const filteredInvoices = invoices
    .filter(inv => {
      const matchesSearch = searchQuery === '' || 
        (inv.invoice_number && inv.invoice_number.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (inv.client?.name && inv.client.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (inv.client?.company_name && inv.client.company_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (inv.client?.company?.name && inv.client.company.name.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // Newest first
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, startIndex + itemsPerPage);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, itemsPerPage]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatCurrencyCompact = (amount) => {
    if (amount >= 1000) {
      return '$' + (amount / 1000).toFixed(1) + 'k';
    }
    return '$' + amount.toFixed(0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateCompact = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="invoices-tab">
      {/* Stats Cards - 5 cards on desktop, 2 cols on mobile */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-5 md:gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-blue-500/20 rounded-lg shrink-0">
                <DollarSign className="h-4 w-4 md:h-5 md:w-5 text-blue-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-400 truncate">Outstanding</p>
                <p className="text-sm md:text-lg font-semibold text-white truncate">
                  <span className="md:hidden">{formatCurrencyCompact(stats?.outstanding || 0)}</span>
                  <span className="hidden md:inline">{formatCurrency(stats?.outstanding || 0)}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-red-500/20 rounded-lg shrink-0">
                <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-red-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-400 truncate">Overdue</p>
                <p className="text-sm md:text-lg font-semibold text-white truncate">
                  <span className="md:hidden">{formatCurrencyCompact(stats?.overdue || 0)}</span>
                  <span className="hidden md:inline">{formatCurrency(stats?.overdue || 0)}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-green-500/20 rounded-lg shrink-0">
                <TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-green-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-400 truncate">This Week</p>
                <p className="text-sm md:text-lg font-semibold text-white truncate">
                  <span className="md:hidden">{formatCurrencyCompact(stats?.this_week || 0)}</span>
                  <span className="hidden md:inline">{formatCurrency(stats?.this_week || 0)}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-purple-500/20 rounded-lg shrink-0">
                <FileText className="h-4 w-4 md:h-5 md:w-5 text-purple-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-400 truncate">This Month</p>
                <p className="text-sm md:text-lg font-semibold text-white truncate">
                  <span className="md:hidden">{formatCurrencyCompact(stats?.this_month || 0)}</span>
                  <span className="hidden md:inline">{formatCurrency(stats?.this_month || 0)}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-800/50 border-slate-700 col-span-2 md:col-span-1">
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-emerald-500/20 rounded-lg shrink-0">
                <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-emerald-400" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] md:text-xs text-slate-400 truncate">Total Collected</p>
                <p className="text-sm md:text-lg font-semibold text-white truncate">
                  <span className="md:hidden">{formatCurrencyCompact(stats?.total_collected || 0)}</span>
                  <span className="hidden md:inline">{formatCurrency(stats?.total_collected || 0)}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar - Stacked on mobile */}
      <div className="space-y-3 md:space-y-0 md:flex md:items-center md:justify-between md:gap-4">
        {/* Search and Filter */}
        <div className="flex gap-2 w-full md:w-auto md:flex-1 md:max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-slate-800/50 border-slate-700 text-white text-sm h-9 md:h-10"
              data-testid="invoice-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2 md:px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-md text-white text-xs md:text-sm h-9 md:h-10"
            data-testid="invoice-status-filter"
          >
            <option value="all">All</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="viewed">Viewed</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-2 w-full md:w-auto">
          <Button 
            variant="outline" 
            onClick={() => setShowSettingsDialog(true)}
            className="border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs md:text-sm h-9 md:h-10 px-2 md:px-3"
            data-testid="invoice-settings-btn"
            title="Invoice Branding Settings"
          >
            <Settings className="h-4 w-4 md:mr-1" />
            <span className="hidden md:inline">Settings</span>
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowThemeDialog(true)}
            className="border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs md:text-sm h-9 md:h-10 px-2 md:px-3"
            data-testid="invoice-theme-btn"
            title="Invoice Theme"
          >
            <Palette className="h-4 w-4 md:mr-1" />
            <span className="hidden md:inline">Theme</span>
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowStripeConnectDialog(true)}
            className={`border-slate-600 dark:border-slate-700 text-xs md:text-sm h-9 md:h-10 px-2 md:px-3 ${
              stripeConnectStatus?.charges_enabled 
                ? 'text-green-600 dark:text-green-400 border-green-600/50 dark:border-green-500/50 hover:bg-green-50 dark:hover:bg-green-900/20' 
                : 'text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white'
            }`}
            data-testid="stripe-connect-btn"
            title={stripeConnectStatus?.charges_enabled ? 'Online Payments Enabled' : 'Setup Online Payments'}
          >
            <CreditCard className="h-4 w-4 md:mr-1" />
            <span className="hidden md:inline">
              {stripeConnectStatus?.charges_enabled ? 'Payments' : 'Payments'}
            </span>
            {stripeConnectStatus?.charges_enabled && (
              <CheckCircle className="h-3 w-3 ml-1 text-green-500" />
            )}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowClientsDialog(true)}
            className="flex-1 md:flex-none border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs md:text-sm h-9 md:h-10 px-3"
            data-testid="manage-clients-btn"
          >
            Clients ({clients.length})
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowStatementsDialog(true)}
            className="flex-1 md:flex-none border-slate-600 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white text-xs md:text-sm h-9 md:h-10 px-3"
            data-testid="statements-btn"
          >
            Statements
          </Button>
          <Button 
            onClick={handleCreateInvoice}
            className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-700 text-xs md:text-sm h-9 md:h-10 px-3"
            data-testid="new-invoice-btn"
          >
            <span className="sm:hidden">New</span>
            <span className="hidden sm:inline">New Invoice</span>
          </Button>
        </div>
      </div>

      {/* Invoice List */}
      {paginatedInvoices.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="py-8 md:py-12 text-center">
            <h3 className="text-base md:text-lg font-medium text-white mb-2">
              {invoices.length === 0 ? 'No invoices yet' : 'No matching invoices'}
            </h3>
            <p className="text-sm text-slate-400 mb-4">
              {invoices.length === 0 
                ? 'Create your first invoice to start billing'
                : 'Try adjusting your search or filter'}
            </p>
            {invoices.length === 0 && (
              <Button onClick={handleCreateInvoice} className="bg-blue-600 hover:bg-blue-700">
                Create Invoice
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop Table View */}
          <Card className="hidden md:block bg-slate-800/50 border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Invoice</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Balance</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Due Date</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {paginatedInvoices.map((invoice) => {
                    return (
                      <tr 
                        key={invoice.id} 
                        className="hover:bg-slate-700/30 cursor-pointer"
                        onClick={() => handleViewInvoice(invoice)}
                        data-testid={`invoice-row-${invoice.id}`}
                      >
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm text-white">
                            {invoice.invoice_number || <span className="text-slate-400 italic">Draft</span>}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-white text-sm">{invoice.client?.company?.name || invoice.client?.name || 'Unknown'}</p>
                            {invoice.client?.company ? (
                              invoice.client.name && <p className="text-slate-400 text-xs">{invoice.client.name}{invoice.client.title ? ` (${invoice.client.title})` : ''}</p>
                            ) : invoice.client?.company_name && (
                              <p className="text-slate-400 text-xs">{invoice.client.company_name}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-white font-medium">{formatCurrency(invoice.balance_due ?? invoice.total)}</span>
                          {invoice.total_paid > 0 && (
                            <span className="block text-xs text-slate-500">of {formatCurrency(invoice.total)}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${statusConfig[invoice.status]?.color || 'bg-slate-500'} text-white`}>
                            {statusConfig[invoice.status]?.label || invoice.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-slate-300 text-sm">{formatDate(invoice.due_date)}</span>
                        </td>
                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <InvoiceActions 
                            invoice={invoice}
                            onView={handleViewInvoice}
                            onEdit={handleEditInvoice}
                            onSend={handleSendInvoice}
                            onMarkPaid={handleMarkPaid}
                            onDuplicate={handleDuplicateInvoice}
                            onDelete={handleDeleteInvoice}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-2">
            {paginatedInvoices.map((invoice) => {
              return (
                <Card 
                  key={invoice.id}
                  className="bg-slate-800/50 border-slate-700 cursor-pointer active:bg-slate-700/50"
                  onClick={() => handleViewInvoice(invoice)}
                  data-testid={`invoice-card-${invoice.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      {/* Left side - Invoice info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-slate-400">{invoice.invoice_number}</span>
                          <Badge className={`${statusConfig[invoice.status]?.color || 'bg-slate-500'} text-white text-[10px] px-1.5 py-0`}>
                            {statusConfig[invoice.status]?.label || invoice.status}
                          </Badge>
                        </div>
                        <p className="text-white text-sm font-medium truncate">
                          {invoice.client?.company?.name || invoice.client?.name || 'Unknown'}
                        </p>
                        {invoice.client?.company ? (
                          invoice.client.name && <p className="text-slate-400 text-xs truncate">{invoice.client.name}{invoice.client.title ? ` (${invoice.client.title})` : ''}</p>
                        ) : invoice.client?.company_name && (
                          <p className="text-slate-400 text-xs truncate">{invoice.client.company_name}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                          <span className="font-semibold text-white">{formatCurrency(invoice.balance_due ?? invoice.total)}</span>
                          {invoice.total_paid > 0 && <span className="text-teal-400">({formatCurrency(invoice.total_paid)} paid)</span>}
                          <span>Due {formatDateCompact(invoice.due_date)}</span>
                        </div>
                      </div>
                      
                      {/* Right side - Actions */}
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <InvoiceActions 
                          invoice={invoice}
                          onView={handleViewInvoice}
                          onEdit={handleEditInvoice}
                          onSend={handleSendInvoice}
                          onMarkPaid={handleMarkPaid}
                          onDuplicate={handleDuplicateInvoice}
                          onDelete={handleDeleteInvoice}
                          compact
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Pagination Controls */}
          {filteredInvoices.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-700">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <span>Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>per page</span>
                <span className="hidden sm:inline text-slate-500">
                  • Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredInvoices.length)} of {filteredInvoices.length}
                </span>
              </div>
              
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
                  >
                    Prev
                  </Button>
                  <span className="px-3 text-sm text-slate-300">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
                  >
                    Last
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Dialogs */}
      <ClientsDialog
        open={showClientsDialog}
        onOpenChange={setShowClientsDialog}
        clients={clients}
        onClientsChange={loadData}
      />
      
      <InvoiceDialog
        open={showInvoiceDialog}
        onOpenChange={setShowInvoiceDialog}
        invoice={editingInvoice}
        clients={clients}
        profile={profile}
        invoiceSettings={invoiceSettings}
        onSave={(savedInvoice) => {
          loadData();
          // Land the user ON the invoice they just created/edited (View dialog),
          // instead of dumping them back to the list.
          if (savedInvoice) {
            setAutoOpenPayment(false);
            setSelectedInvoice(savedInvoice);
            setShowViewDialog(true);
          }
        }}
        onClientsChange={loadData}
      />
      
      <InvoiceViewDialog
        open={showViewDialog}
        onOpenChange={(o) => { setShowViewDialog(o); if (!o) setAutoOpenPayment(false); }}
        invoice={selectedInvoice}
        profile={profile}
        invoiceSettings={invoiceSettings}
        autoOpenPayment={autoOpenPayment}
        onAction={(action, invoice) => {
          if (action === 'addCharges' && invoice) {
            // Close view dialog and open edit dialog for adding supplemental charges
            setShowViewDialog(false);
            setEditingInvoice(invoice);
            setShowInvoiceDialog(true);
          } else if (action === 'edit' && invoice) {
            // Close view dialog and open edit dialog
            setShowViewDialog(false);
            setEditingInvoice(invoice);
            setShowInvoiceDialog(true);
          } else {
            // Default: refresh data
            loadData();
          }
        }}
      />
      
      <InvoiceSettingsDialog
        open={showSettingsDialog}
        onOpenChange={setShowSettingsDialog}
        onSave={() => {
          loadData();
          loadInvoiceSettings();
        }}
      />
      
      <InvoiceThemeDialog
        open={showThemeDialog}
        onOpenChange={setShowThemeDialog}
        onSave={() => {
          loadInvoiceSettings();
        }}
      />

      <StripeConnectDialog
        open={showStripeConnectDialog}
        onOpenChange={setShowStripeConnectDialog}
        onStatusChange={setStripeConnectStatus}
      />

      <StatementsDialog
        open={showStatementsDialog && !viewingStatement}
        onClose={() => setShowStatementsDialog(false)}
        clients={clients}
        profile={profile}
        invoiceSettings={invoiceSettings}
        onViewStatement={(statement) => {
          setViewingStatement(statement);
        }}
        onSendStatement={(statement) => {
          setSendingStatement(statement);
        }}
      />

      <StatementViewDialog
        open={!!viewingStatement}
        onOpenChange={(open) => {
          if (!open) {
            setViewingStatement(null);
            setShowStatementsDialog(true);
          }
        }}
        statement={viewingStatement}
        profile={profile}
        invoiceSettings={invoiceSettings}
        onAction={() => {
          setViewingStatement(null);
          setShowStatementsDialog(true);
        }}
      />

      {/* Send Invoice Dialog with CC support */}
      <SendInvoiceDialog
        open={!!sendingInvoice}
        onOpenChange={(open) => !open && setSendingInvoice(null)}
        invoice={sendingInvoice}
        client={sendingInvoice?.client}
        onSent={() => {
          setSendingInvoice(null);
          loadData();
        }}
      />

      {/* Send Statement Dialog with CC support */}
      <SendStatementDialog
        open={!!sendingStatement}
        onOpenChange={(open) => !open && setSendingStatement(null)}
        statement={sendingStatement}
        onSent={() => {
          setSendingStatement(null);
        }}
      />
    </div>
  );
}

// Extracted Actions component for reuse
function InvoiceActions({ invoice, onView, onEdit, onSend, onMarkPaid, onDuplicate, onDelete, compact }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className={compact ? "h-7 w-7 p-0" : "h-8 w-8 p-0"} data-testid={`invoice-actions-trigger-${invoice.id}`}>
          <MoreHorizontal className={compact ? "h-4 w-4 text-slate-400" : "h-4 w-4 text-slate-400"} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
        <DropdownMenuItem 
          onClick={() => onView(invoice)}
          className="text-slate-200 focus:bg-slate-700"
          data-testid={`invoice-action-view-${invoice.id}`}
        >
          <Eye className="h-4 w-4 mr-2" />
          View
        </DropdownMenuItem>
        {/* Allow editing for all statuses except void */}
        {invoice.status !== 'void' && (
          <DropdownMenuItem 
            onClick={() => onEdit(invoice)}
            className="text-slate-200 focus:bg-slate-700"
          >
            <FileText className="h-4 w-4 mr-2" />
            Edit
          </DropdownMenuItem>
        )}
        {invoice.status === 'draft' && (
          <DropdownMenuItem 
            onClick={() => onSend(invoice)}
            className="text-slate-200 focus:bg-slate-700"
          >
            <Send className="h-4 w-4 mr-2" />
            Send
          </DropdownMenuItem>
        )}
        {invoice.status === 'sent' && (
          <DropdownMenuItem 
            onClick={() => onSend(invoice)}
            className="text-slate-200 focus:bg-slate-700"
          >
            <Send className="h-4 w-4 mr-2" />
            Resend
          </DropdownMenuItem>
        )}
        {['sent', 'viewed', 'overdue'].includes(invoice.status) && (
          <DropdownMenuItem 
            onClick={() => onMarkPaid(invoice)}
            className="text-green-400 focus:bg-slate-700"
            data-testid={`invoice-action-mark-paid-${invoice.id}`}
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Mark Paid
          </DropdownMenuItem>
        )}
        <DropdownMenuItem 
          onClick={() => onDuplicate(invoice)}
          className="text-slate-200 focus:bg-slate-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Duplicate
        </DropdownMenuItem>
        {invoice.status === 'draft' && (
          <DropdownMenuItem 
            onClick={() => onDelete(invoice)}
            className="text-red-400 focus:bg-slate-700"
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            Delete
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
